import {
  atomFamily,
  selectorFamily,
  useRecoilState,
  useRecoilValue,
  useSetRecoilState,
} from "recoil";
import { TComponentType } from "../utils/types/common/components.types";
import { USE_SELECTED_INDEX } from "./useSelectedElement";
import { TUseXElement } from "./useXElement";

export type TCommonElementIndex =
  | "HEADER"
  | "FOOTER"
  | "PAGE"
  | "ARTICLE"
  | "CATEGORY_FILTER"
  | "PRODUCT_DETAILS";

export const COMMON_ELEMENT_INDEXES: TCommonElementIndex[] = [
  "HEADER",
  "FOOTER",
  "ARTICLE",
  // TODO IMPLEMENT ALL OTHER COMMON ELEMENTS
  // "PAGE",
  // "CATEGORY_FILTER",
  // "PRODUCT_DETAILS",
];

export const isCommon = (index: string, root: boolean = false) => {
  if (root)
    return COMMON_ELEMENT_INDEXES.includes(index as TCommonElementIndex);

  let found = false;

  for (let i = 0; i < COMMON_ELEMENT_INDEXES.length && !found; i++) {
    const element = COMMON_ELEMENT_INDEXES[i];

    found = index.startsWith(element);
  }

  return found;
};

export const getRef = (index: string) => {
  let ref: TCommonElementIndex | null = null;

  if (!index) return ref;

  for (let i = 0; i < COMMON_ELEMENT_INDEXES.length && ref === null; i++) {
    const element = COMMON_ELEMENT_INDEXES[i];

    if (index.startsWith(element)) {
      ref = element;
    }
  }

  return ref;
};

// THIS IS USED TO LIST ALL THE ELEMENTS PARENT AND CHILD
// THIS HAS BEEN CREATED TO STORE ALL COMMON ELEMENTS AND CHILDREN
export const USE_X_COMMON = atomFamily<
  TUseXElement<any> | null,
  TCommonElementIndex | string
>({
  key: "USE_X_COMMON",
  default: null,
});

export const useXCommon = (index: TCommonElementIndex | string) => {
  const [xCommon, setXCommon] = useRecoilState(USE_X_COMMON(index));

  return { xCommon, setXCommon };
};

export function useXCommonUpdater(index: TCommonElementIndex | string) {
  return useSetRecoilState(USE_X_COMMON(index));
}

export function useXCommonValue(index: TCommonElementIndex | string) {
  return useRecoilValue(USE_X_COMMON(index));
}

// THIS IS USED FOR THE CHILDREN
export const USE_X_COMMON_CHILDREN = atomFamily<
  TComponentType[],
  TCommonElementIndex
>({
  key: "USE_X_COMMON_CHILDREN",
  default: [],
});

export const USE_X_COMMON_CHILDREN_SELECTOR = selectorFamily<
  TComponentType[],
  string
>({
  key: "USE_X_COMMON_CHILDREN_SELECTOR",
  get: (id: string) => (opts) => {
    const ref = getRef(id);

    if (!ref) return [];

    return opts.get(USE_X_COMMON_CHILDREN(ref));
  },
  set: (id: string) => (opts, newValue) => {
    const selectedId = opts.get(USE_SELECTED_INDEX);

    const ref = getRef(id ?? selectedId);

    if (!ref) return;

    return opts.set(USE_X_COMMON_CHILDREN(ref), newValue);
  },
});

export const useXCommonChildren = (index: TCommonElementIndex) => {
  const [xChildren, setXChildren] = useRecoilState(
    USE_X_COMMON_CHILDREN_SELECTOR(index)
  );

  return { xChildren, setXChildren };
};

export function useXCommonChildrenUpdater(index: TCommonElementIndex) {
  return useSetRecoilState(USE_X_COMMON_CHILDREN_SELECTOR(index));
}

export function useXCommonChildrenValue(index: TCommonElementIndex) {
  return useRecoilValue(USE_X_COMMON_CHILDREN_SELECTOR(index));
}
